import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Box, RefreshCw, RotateCcw, ChevronLeft, ChevronRight, X, Maximize2, Minimize2 } from 'lucide-react';

const occupancyColor = (items) => {
  if (items === 0) return { bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.25)', text: '#94a3b8', glow: 'transparent' };
  if (items === 1) return { bg: 'rgba(34,197,94,0.18)', border: 'rgba(34,197,94,0.5)', text: '#22c55e', glow: 'rgba(34,197,94,0.15)' };
  if (items <= 3) return { bg: 'rgba(59,130,246,0.18)', border: 'rgba(59,130,246,0.5)', text: '#3b82f6', glow: 'rgba(59,130,246,0.15)' };
  return { bg: 'rgba(249,115,22,0.22)', border: 'rgba(249,115,22,0.6)', text: '#f97316', glow: 'rgba(249,115,22,0.15)' };
};

// Touch/drag hook for 3D rotation
const useDragRotation = (initialX, initialY) => {
  const [rot, setRot] = useState({ x: initialX, y: initialY });
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const onStart = useCallback((clientX, clientY) => {
    dragging.current = true;
    lastPos.current = { x: clientX, y: clientY };
  }, []);

  const onMove = useCallback((clientX, clientY) => {
    if (!dragging.current) return;
    const dx = clientX - lastPos.current.x;
    const dy = clientY - lastPos.current.y;
    lastPos.current = { x: clientX, y: clientY };
    setRot(prev => ({
      x: Math.max(-40, Math.min(50, prev.x - dy * 0.4)),
      y: Math.max(-70, Math.min(70, prev.y + dx * 0.4)),
    }));
  }, []);

  const onEnd = useCallback(() => { dragging.current = false; }, []);

  const reset = useCallback(() => { setRot({ x: initialX, y: initialY }); }, [initialX, initialY]);

  return { rot, onStart, onMove, onEnd, reset, setRot };
};

export default function Cabinet3DPage() {
  const [bins, setBins] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCabinet, setSelectedCabinet] = useState('');
  const [selectedPos, setSelectedPos] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const carouselRef = useRef(null);
  const sceneRef = useRef(null);

  const { rot, onStart, onMove, onEnd, reset } = useDragRotation(18, -22);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [bRes, sRes] = await Promise.all([api.get('/bins'), api.get('/stock')]);
      setBins(bRes.data);
      setStockData(sRes.data);
    } catch { toast.error('Eroare la incarcarea datelor'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Touch handlers for 3D scene
  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return;
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        onStart(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const handleTouchMove = (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        onMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const handleTouchEnd = () => onEnd();
    const handleMouseDown = (e) => onStart(e.clientX, e.clientY);
    const handleMouseMove = (e) => onMove(e.clientX, e.clientY);
    const handleMouseUp = () => onEnd();

    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);
    el.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onStart, onMove, onEnd]);

  const cabinets = [...new Set(bins.map(b => b.cabinet).filter(Boolean))].sort();
  const activeCabinet = selectedCabinet || cabinets[0] || '';
  const cabinetBins = bins.filter(b => b.cabinet === activeCabinet);

  // Group by shelf
  const shelves = {};
  cabinetBins.forEach(b => {
    const s = b.shelf || '-';
    if (!shelves[s]) shelves[s] = [];
    shelves[s].push(b);
  });
  Object.values(shelves).forEach(arr => arr.sort((a, b) => (a.position || '').localeCompare(b.position || '')));
  const shelfNames = Object.keys(shelves).sort().reverse();

  const getStockCount = (binId) => stockData.filter(s => s.bin_id === binId && s.quantity > 0).length;
  const getStockItems = (binId) => stockData.filter(s => s.bin_id === binId && s.quantity > 0);

  const maxPositions = Math.max(...Object.values(shelves).map(arr => arr.length), 1);
  const shelfCount = shelfNames.length || 1;

  // Responsive cell sizes
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const cellW = isMobile ? 48 : 60;
  const cellH = isMobile ? 34 : 40;
  const cellD = isMobile ? 35 : 50;
  const cabinetW = maxPositions * (cellW + 4) + 20;
  const cabinetH = shelfCount * (cellH + 8) + 20;

  const cabinetIdx = cabinets.indexOf(activeCabinet);
  const prevCabinet = () => { if (cabinetIdx > 0) { setSelectedCabinet(cabinets[cabinetIdx - 1]); setSelectedPos(null); } };
  const nextCabinet = () => { if (cabinetIdx < cabinets.length - 1) { setSelectedCabinet(cabinets[cabinetIdx + 1]); setSelectedPos(null); } };

  // Scroll carousel to active cabinet
  useEffect(() => {
    if (carouselRef.current && activeCabinet) {
      const btn = carouselRef.current.querySelector(`[data-cab="${activeCabinet}"]`);
      if (btn) btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeCabinet]);

  // Stats for current cabinet
  const totalPositions = cabinetBins.length;
  const occupiedPositions = cabinetBins.filter(b => getStockCount(b.id) > 0).length;
  const occupancyPct = totalPositions > 0 ? Math.round((occupiedPositions / totalPositions) * 100) : 0;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
    </div>
  );

  const sceneWrapper = (
    <div className={fullscreen ? 'fixed inset-0 z-50 bg-slate-950 flex flex-col' : ''}>
      {fullscreen && (
        <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Box className="w-5 h-5 text-indigo-400" />
            <span className="text-white font-bold text-sm">{activeCabinet}</span>
            <span className="text-slate-400 text-xs">({occupancyPct}% ocupat)</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={reset} className="text-slate-400 hover:text-white h-8 w-8 p-0">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setFullscreen(false)} className="text-slate-400 hover:text-white h-8 w-8 p-0">
              <Minimize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* 3D Scene */}
      <div
        ref={sceneRef}
        className={`flex items-center justify-center select-none cursor-grab active:cursor-grabbing ${fullscreen ? 'flex-1' : ''}`}
        style={{
          perspective: '1200px',
          minHeight: fullscreen ? undefined : (isMobile ? 280 : 380),
          touchAction: 'none',
          background: fullscreen ? 'radial-gradient(circle at center, #1e293b 0%, #0f172a 100%)' : undefined,
        }}
        data-testid="cabinet-3d-scene"
      >
        <div style={{
          transformStyle: 'preserve-3d',
          transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`,
          transition: 'transform 0.08s linear',
          width: cabinetW + 40,
          height: cabinetH + 40,
          position: 'relative',
        }}>
          {/* Cabinet frame */}
          <div style={{
            position: 'absolute', top: 0, left: 0,
            width: cabinetW + 40, height: cabinetH + 40,
            background: fullscreen
              ? 'linear-gradient(145deg, #334155, #1e293b)'
              : 'linear-gradient(145deg, #f8fafc, #e2e8f0)',
            border: fullscreen ? '2px solid #475569' : '2px solid #cbd5e1',
            borderRadius: 12,
            boxShadow: fullscreen ? '0 20px 80px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.15)',
            transformStyle: 'preserve-3d',
          }}>
            {/* Back panel */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: fullscreen
                ? 'linear-gradient(180deg, #1e293b, #0f172a)'
                : 'linear-gradient(180deg, #f1f5f9, #e2e8f0)',
              borderRadius: 10,
              transform: `translateZ(-${cellD}px)`,
            }} />
            {/* Side panels */}
            <div style={{
              position: 'absolute', top: 0, left: 0, width: cellD, height: '100%',
              background: fullscreen
                ? 'linear-gradient(90deg, #1e293b, #334155)'
                : 'linear-gradient(90deg, #e2e8f0, #cbd5e1)',
              transformOrigin: 'left', transform: 'rotateY(-90deg)',
              borderRadius: '0 10px 10px 0',
            }} />
            <div style={{
              position: 'absolute', top: 0, right: 0, width: cellD, height: '100%',
              background: fullscreen
                ? 'linear-gradient(90deg, #334155, #1e293b)'
                : 'linear-gradient(90deg, #cbd5e1, #e2e8f0)',
              transformOrigin: 'right', transform: 'rotateY(90deg)',
              borderRadius: '10px 0 0 10px',
            }} />

            {/* Shelves */}
            {shelfNames.map((shelfName, si) => {
              const positions = shelves[shelfName] || [];
              const shelfY = 15 + si * (cellH + 8);
              return (
                <React.Fragment key={shelfName}>
                  <div style={{
                    position: 'absolute', left: -30, top: shelfY + 8,
                    fontSize: isMobile ? 9 : 10, fontWeight: 700,
                    color: fullscreen ? '#94a3b8' : '#64748b',
                    transform: 'translateZ(10px)',
                  }}>{shelfName}</div>

                  <div style={{
                    position: 'absolute', left: 10, top: shelfY + cellH + 2,
                    width: cabinetW + 20, height: 3,
                    background: fullscreen
                      ? 'linear-gradient(90deg, #475569, #64748b, #475569)'
                      : 'linear-gradient(90deg, #94a3b8, #64748b, #94a3b8)',
                    borderRadius: 2,
                    transformStyle: 'preserve-3d',
                    transform: 'translateZ(5px)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }} />

                  {positions.map((pos, pi) => {
                    const items = getStockCount(pos.id);
                    const c = occupancyColor(items);
                    const isSelected = selectedPos === pos.id;
                    const posX = 15 + pi * (cellW + 4);

                    return (
                      <div key={pos.id}
                        onClick={(e) => { e.stopPropagation(); setSelectedPos(isSelected ? null : pos.id); }}
                        onMouseEnter={() => !isMobile && setSelectedPos(pos.id)}
                        onMouseLeave={() => !isMobile && setSelectedPos(null)}
                        style={{
                          position: 'absolute',
                          left: posX, top: shelfY,
                          width: cellW, height: cellH,
                          transformStyle: 'preserve-3d',
                          transform: `translateZ(${isSelected ? 22 : 5}px) ${isSelected ? 'scale(1.1)' : ''}`,
                          transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          cursor: 'pointer',
                          zIndex: isSelected ? 10 : 1,
                        }}
                        data-testid={`pos3d-${pos.code}`}
                      >
                        {/* Front face */}
                        <div style={{
                          position: 'absolute', width: '100%', height: '100%',
                          background: c.bg, border: `2px solid ${isSelected ? c.text : c.border}`,
                          borderRadius: 6, display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          boxShadow: isSelected
                            ? `0 8px 30px ${c.glow}, 0 0 0 2px ${c.text}`
                            : '0 2px 8px rgba(0,0,0,0.08)',
                          transition: 'all 0.2s',
                        }}>
                          <span style={{ fontSize: isMobile ? 9 : 11, fontWeight: 700, color: c.text, fontFamily: 'monospace' }}>
                            {pos.position}
                          </span>
                          {items > 0 && (
                            <span style={{ fontSize: isMobile ? 8 : 9, color: c.text, marginTop: 1 }}>
                              {items} art.
                            </span>
                          )}
                        </div>
                        {/* Top face */}
                        <div style={{
                          position: 'absolute', width: '100%', height: 10,
                          background: c.bg,
                          borderTop: `1px solid ${c.border}`, borderLeft: `1px solid ${c.border}`, borderRight: `1px solid ${c.border}`,
                          transformOrigin: 'bottom', transform: 'rotateX(90deg)',
                          top: -10, borderRadius: '4px 4px 0 0', opacity: 0.6,
                        }} />
                        {/* Right face */}
                        <div style={{
                          position: 'absolute', width: 10, height: '100%',
                          background: c.bg,
                          borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}`, borderRight: `1px solid ${c.border}`,
                          transformOrigin: 'left', transform: 'rotateY(90deg)',
                          right: -10, borderRadius: '0 4px 4px 0', opacity: 0.5,
                        }} />
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Cabinet title under 3D */}
            <div style={{
              position: 'absolute', bottom: -32, left: '50%',
              transform: 'translateX(-50%) translateZ(30px)',
              fontSize: isMobile ? 12 : 14, fontWeight: 800,
              color: fullscreen ? '#e2e8f0' : '#334155',
              background: fullscreen ? '#1e293b' : 'white',
              padding: '3px 14px', borderRadius: 8,
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              border: fullscreen ? '1px solid #475569' : '1px solid #e2e8f0',
              whiteSpace: 'nowrap',
            }}>{activeCabinet}</div>
          </div>
        </div>
      </div>

      {/* Drag hint */}
      {!fullscreen && (
        <p className="text-center text-[10px] text-slate-400 mt-1 select-none">
          Trageti cu degetul / mouse-ul pentru a roti dulapul
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6" data-testid="cabinet3d-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="cabinet3d-title">
            <Box className="w-6 h-6 text-indigo-600" /> Vizualizare 3D
          </h1>
          <p className="text-xs md:text-sm text-slate-500 mt-0.5">Reprezentare 3D a dulapurilor - tap pe pozitie pentru detalii</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reset} className="h-8 w-8 p-0" title="Reseteaza rotatie" data-testid="reset-rotation-btn">
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setFullscreen(true)} className="h-8 w-8 p-0" title="Fullscreen" data-testid="fullscreen-btn">
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Cabinet Carousel - swipeable on mobile */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={prevCabinet} disabled={cabinetIdx <= 0}
          className="flex-shrink-0 h-9 w-9 p-0 rounded-full" data-testid="prev-cabinet-btn">
          <ChevronLeft className="w-5 h-5" />
        </Button>

        <div
          ref={carouselRef}
          className="flex-1 overflow-x-auto no-scrollbar"
          style={{ WebkitOverflowScrolling: 'touch', scrollSnapType: 'x mandatory' }}
        >
          <div className="flex gap-2 px-1 py-1" style={{ minWidth: 'min-content' }}>
            {cabinets.map(c => {
              const cabBins = bins.filter(b => b.cabinet === c);
              const occ = cabBins.filter(b => getStockCount(b.id) > 0).length;
              const pct = cabBins.length > 0 ? Math.round((occ / cabBins.length) * 100) : 0;
              return (
                <button
                  key={c}
                  data-cab={c}
                  onClick={() => { setSelectedCabinet(c); setSelectedPos(null); }}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold border transition-all active:scale-95 ${
                    c === activeCabinet
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}
                  style={{ scrollSnapAlign: 'center' }}
                  data-testid={`cab3d-${c}`}
                >
                  <span className="block">{c}</span>
                  <span className={`block text-[9px] mt-0.5 ${c === activeCabinet ? 'text-indigo-200' : 'text-slate-400'}`}>
                    {pct}% ocupat
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <Button variant="ghost" size="sm" onClick={nextCabinet} disabled={cabinetIdx >= cabinets.length - 1}
          className="flex-shrink-0 h-9 w-9 p-0 rounded-full" data-testid="next-cabinet-btn">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-xl border border-slate-100">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-medium text-slate-500">{activeCabinet} - Ocupare</span>
            <span className="text-xs font-bold text-indigo-600">{occupancyPct}%</span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${occupancyPct}%`,
                background: occupancyPct > 80 ? '#f97316' : occupancyPct > 50 ? '#3b82f6' : '#22c55e',
              }}
            />
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs font-bold text-slate-800">{occupiedPositions}/{totalPositions}</p>
          <p className="text-[9px] text-slate-400">pozitii</p>
        </div>
      </div>

      {/* 3D Scene (normal mode) */}
      <div className="bg-gradient-to-b from-slate-50 to-white rounded-2xl border border-slate-200 overflow-hidden p-4">
        {sceneWrapper}
      </div>

      {/* Selected Position Detail (tap on mobile) */}
      {selectedPos && (() => {
        const pos = bins.find(b => b.id === selectedPos);
        const items = getStockItems(selectedPos);
        if (!pos) return null;
        return (
          <div
            className="fixed bottom-4 left-3 right-3 lg:left-auto lg:right-6 lg:w-72 bg-white rounded-2xl border border-slate-200 shadow-2xl p-4 z-50 animate-in slide-in-from-bottom-4"
            data-testid="pos3d-tooltip"
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="font-mono font-bold text-slate-800 text-sm">{pos.code}</h4>
                <p className="text-[10px] text-slate-400">{pos.cabinet} &rarr; {pos.shelf} &rarr; {pos.position}</p>
              </div>
              <button onClick={() => setSelectedPos(null)} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="close-tooltip-btn">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            {items.length === 0 ? (
              <div className="py-3 text-center">
                <p className="text-xs text-slate-400">Pozitie goala</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1.5 rounded-lg">
                    <span className="truncate text-slate-700 flex-1 font-medium">{it.article_name || it.article_code}</span>
                    <Badge className="ml-2 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{it.quantity} {it.unit || 'buc'}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Fullscreen overlay */}
      {fullscreen && sceneWrapper}

      {/* Legend */}
      <div className="flex items-center justify-center gap-3 md:gap-4 text-[10px] md:text-xs text-slate-500 flex-wrap">
        {[
          { items: 0, label: 'Gol' },
          { items: 1, label: '1 articol' },
          { items: 2, label: '2-3 articole' },
          { items: 4, label: '4+ articole' },
        ].map(({ items, label }) => {
          const c = occupancyColor(items);
          return (
            <span key={label} className="flex items-center gap-1">
              <span className="w-3 h-3 md:w-4 md:h-4 rounded" style={{ background: c.bg, border: `1px solid ${c.border}` }} />
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
}
